import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { BaseScene } from '../../scenes/BaseScene.js';
import { PlatformerBoard } from './PlatformerBoard.js';
import { PlatformerCamera } from './PlatformerCamera.js';
import { PlatformerLighting } from './PlatformerLighting.js';
import {
    spawnEntity,
    PlayerEntity,
    MushroomEntity,
    carryRiders,
} from './PlatformerEntities.js';
import { masksInteract, shapesOverlap } from './PlatformerCollision.js';
import { PlatformerNotes } from './PlatformerNotes.js';
import { PlatformerJuice } from './PlatformerJuice.js';
import {
    PLATFORMER_GRID,
    PLATFORMER_PLAYER,
    PLATFORMER_SCORING,
    PLATFORMER_LAYERS,
    PLATFORMER_TILE_DEFS,
} from './platformerConfig.js';
import { buildHud, buildChrome, refreshHud, showGameOver, showWin } from './PlatformerSceneUi.js';
import { preloadStageAssets, createLoadingGate } from './PlatformerPreloader.js';
import { NoteAudioPlayer } from '../../core/NoteAudioPlayer.js';
import { loadStage } from '../../services/StageLoader.js';

/**
 * Classic-Mario-style 2D platformer scene.
 *
 * Boots in three phases:
 *
 *   1. `init()` — install root containers, build the SceneChrome
 *      (back button + leaderboard) on a HUD layer.
 *   2. `_loadStageAsync()` — fetch `data/stages/platformer/stage01.json`
 *      (or pick up the editor's `pendingStage` when arriving via the
 *      Test Play button), construct a PlatformerBoard, then spawn
 *      every entity in the stage's `entities` layer.
 *   3. `update(delta)` — read the input snapshot, tick the player +
 *      every enemy + every pickup, run pickup/collision sweeps,
 *      scroll the camera, and refresh the HUD.
 *
 * Win condition: player overlaps the flag goal entity → fire confetti,
 * show the LevelCompleteOverlay, optionally chain into the leaderboard
 * submit overlay (same pattern as TrainSim / Snake / Frogger).
 *
 * Lose conditions: fall off the bottom of the world, or take damage
 * while small. A life is consumed; if any remain, the player respawns
 * at the stored spawn cell. When all lives are gone, the GameOverOverlay
 * appears and the back button returns to the menu / editor.
 */
export class PlatformerScene extends BaseScene {
    /** @type {PlatformerBoard|null} */
    _board = null;
    /** Stage JSON the scene is currently rendering. */
    _stage = null;
    /** Pending stage payload set by StageEditorScene before switchScene. */
    pendingStage = null;

    /** Camera-bound world container — entities + tiles are children. */
    _worldContainer = null;
    /** Fixed-position HUD container (lives, coins, score, time). */
    _hudContainer = null;
    /** SceneChrome — back button, leaderboard. */
    _chrome = null;

    /** @type {PlayerEntity|null} */
    _player = null;
    /** All non-player updateables — enemies, mushrooms, coins. */
    _entities = [];
    /** Scripted camera-zoom regions (CameraZoneEntity); not collided. */
    _cameraZones = [];
    /** Moving platforms (MovingPlatformEntity). Stepped BEFORE the
     *  player/entities each fixed step so riders inherit this frame's
     *  delta ("move riders first"); the physics reads the same list
     *  via `board.movingPlatforms` for landings + grounded checks. */
    _platforms = [];
    /** Point lights (PointLightEntity); fed to the lighting compositor. */
    _lights = [];
    /** Music Note entities; managed by PlatformerNotes (not collided as
     *  enemies — the controller does its own per-frame edge detection). */
    _notes = [];
    /** Area triggers with effect='sound' OR effect='music' — both are
     *  edge-triggered; the scene re-arms them by clearing `_inside`
     *  when the player leaves their AABB. */
    _soundZones = [];
    /** Lazily-built audio controller (owns NoteAudioPlayer). */
    _notesController = null;
    /** Follow + zoom camera; positions the world container each frame. */
    _camera = null;
    /** Game-feel layer: SFX, dust, squash & stretch, shake, hit-stop. */
    _juice = null;
    /** Frames the simulation stays frozen after a big hit (hit-stop). */
    _hitStopFrames = 0;
    /** Screen-space multiply lightmap compositor (null until init). */
    _lighting = null;

    /** Stored spawn cell so respawns put the player back home. */
    _spawnCell = { c: 1, r: 1 };
    _startPower = 'small';

    // ── Game state ─────────────────────────────────────────────────
    _lives = PLATFORMER_PLAYER.startingLives;
    _coins = 0;
    _score = 0;
    _timeFrames = 0;
    _won = false;
    _gameOver = false;
    /** Frames remaining of "death freeze" before respawn / game over. */
    _deathFreezeFrames = 0;

    /** Previous-frame keysDown snapshot for edge-detection on jump. */
    _prevJumpDown = false;

    /** Active overlays — kept around so we can clean them up on destroy. */
    _gameOverOverlay = null;
    _winOverlay = null;
    _scoreSubmitOverlay = null;

    /** True after init() finishes wiring containers; the async stage
     *  load fills in the rest of the world once the JSON arrives. */
    _booted = false;
    /** Set by destroy() so an in-flight preload never mounts into a
     *  torn-down scene. */
    _disposed = false;
    /** Pre-warmed audio player handed from the preloader to the next
     *  PlatformerNotes mount (clips already decoded). */
    _preloadedAudio = null;

    init(game) {
        super.init(game);

        this._worldContainer = new Container();
        this._worldContainer.sortableChildren = true;
        this.container.addChild(this._worldContainer);

        this._camera = new PlatformerCamera();

        // Screen-space lightmap (multiply) overlay. Sits ABOVE the world
        // container but BELOW the HUD so darkness/glows affect the level
        // but never dim the score readout. Inert until a stage turns
        // lighting on via its `lighting` block.
        this._lighting = new PlatformerLighting(
            this.game.app?.renderer, this.game.width, this.game.height,
        );
        this._lighting.display.zIndex = 600;
        this.container.addChild(this._lighting.display);

        this._hudContainer = new Container();
        this._hudContainer.zIndex = 1000;
        this.container.sortableChildren = true;
        this.container.addChild(this._hudContainer);

        // Sky-blue underlay so the canvas doesn't flash black during
        // the brief window between scene start and stage load.
        const sky = new Graphics();
        sky.rect(0, 0, this.game.width, this.game.height);
        sky.fill(0x6b9bd2);
        sky.zIndex = -100;
        this.container.addChild(sky);

        // Reset per-run state — leaving these as instance defaults
        // would carry the previous run's score across a restart from
        // the menu. (Scene instances are REUSED across switches, so
        // everything destroy() flips must be re-armed here.)
        this._disposed = false;
        this._lives = this._readSetting('startingLives', PLATFORMER_PLAYER.startingLives);
        this._coins = 0;
        this._score = 0;
        this._timeFrames = 0;
        this._won = false;
        this._gameOver = false;
        this._deathFreezeFrames = 0;
        this._entities = [];
        this._player = null;
        this._prevJumpDown = false;

        buildHud(this);
        buildChrome(this);

        // Editor "Test Play" path: the editor stashes the working
        // draft on `this.pendingStage` before switching scenes. If
        // present, use it directly; otherwise load stage01 from disk.
        // Both paths run through the preload gate — coming from the
        // editor most assets are already cached, so it's near-instant.
        if (this.pendingStage) {
            const stage = this.pendingStage;
            this.pendingStage = null;
            this._startStage(stage);
        } else {
            this._loadStageAsync();
        }

        this._booted = true;
    }

    _readSetting(key, fallback) {
        return this.game.theme?.getSetting?.('platformer', key, fallback) ?? fallback;
    }

    async _loadStageAsync() {
        // Default campaign stage — the Rama Butter level. Falls back to
        // the classic stage01, then to the procedural fallback world.
        const stage = await loadStage('platformer', 'rama01.json')
            || await loadStage('platformer', 'stage01.json');
        if (!stage) console.warn('[PlatformerScene] No stage file — rendering empty world.');
        await this._startStage(stage || this._buildFallbackStage());
    }

    /**
     * Preload every asset the stage references (sprite textures into
     * the SkinResolver cache, audio clips into a NoteAudioPlayer that
     * PlatformerNotes adopts) behind a progress gate, THEN mount. The
     * world therefore appears fully textured on its first frame
     * instead of popping in sprite by sprite.
     */
    async _startStage(stage) {
        this._stage = stage;
        const gate = createLoadingGate(this.game, this.container);
        const audio = new NoteAudioPlayer({ volume: 1 });
        try {
            await preloadStageAssets(stage, this.game.theme, {
                audio,
                onProgress: (f) => gate.setProgress(f),
            });
        } catch (_) { /* best-effort — mount with whatever loaded */ }
        gate.destroy();
        // The scene may have been torn down while assets streamed in
        // (user hit Back during the gate) — don't mount into a corpse.
        if (this._disposed) {
            try { audio.destroy?.(); } catch (_) {}
            return;
        }
        this._preloadedAudio = audio;
        this._mountStage(stage);
    }

    /** Bare-bones default stage in case the JSON file is missing —
     *  shows a flat ground row with the player on it so the scene
     *  isn't a totally empty void. */
    _buildFallbackStage() {
        const grid = PLATFORMER_GRID;
        const tiles = [];
        for (let c = 0; c < grid.cols; c++) {
            tiles.push({ c, r: grid.rows - 1, type: 'ground', rot: 0 });
            tiles.push({ c, r: grid.rows - 2, type: 'ground', rot: 0 });
        }
        return {
            id: 'fallback',
            name: 'Fallback',
            schemaGame: 'platformer',
            schemaVersion: 1,
            grid: { cols: grid.cols, rows: grid.rows, cellSize: grid.cellSize },
            layers: [
                { id: 'sky', label: 'Sky', kind: 'decoration', visible: true, locked: false, tiles: [], entities: [] },
                { id: 'terrain', label: 'Terrain', kind: 'gameplay', visible: true, locked: false, tiles, entities: [] },
                { id: 'entities', label: 'Entities', kind: 'gameplay', visible: true, locked: false, tiles: [], entities: [
                    { id: 'p1', c: 2, r: grid.rows - 3, type: 'player.spawn', props: { startPower: 'small' } },
                    { id: 'flag', c: grid.cols - 3, r: grid.rows - 3, type: 'flag.goal', props: {} },
                ] },
                { id: 'foreground', label: 'Foreground', kind: 'decoration', visible: true, locked: false, tiles: [], entities: [] },
            ],
        };
    }

    _mountStage(stage) {
        if (this._board) {
            if (this._board.container.parent) this._board.container.parent.removeChild(this._board.container);
            this._board.destroy();
        }
        this._entities = [];
        this._cameraZones = [];
        this._platforms = [];
        this._lights = [];
        this._notes = [];
        this._soundZones = [];
        // Fresh notes controller per mount — wipes any pre-loaded
        // audio buffers from the previous stage so swapping levels in
        // the editor doesn't leak decoded clips.
        try { this._notesController?.destroy?.(); } catch (_) {}
        this._notesController = new PlatformerNotes(this.game.theme, {
            audio: this._preloadedAudio || undefined,
        });
        this._preloadedAudio = null;
        this._player = null;

        this._board = new PlatformerBoard(this.game.theme, { editorMode: false });
        this._board.init(stage, PLATFORMER_LAYERS.terrain);
        this._worldContainer.addChild(this._board.container);

        // Stage-wide physics overrides (Phase 5). Merged UNDER the
        // player.spawn's own props so a per-spawn tweak still wins; the
        // player's `_resolvePhysics` then layers the global default
        // under both. Empty / absent → identical to legacy behaviour.
        const stagePhysics = (stage.physics && typeof stage.physics === 'object')
            ? stage.physics : null;

        // Spawn entities — the player goes into a separate slot so
        // the per-frame loop can pull it out without scanning.
        this._board.iterateEntities((record) => {
            const rec = (stagePhysics && record.type === 'player.spawn')
                ? { ...record, props: { ...stagePhysics, ...(record.props || {}) } }
                : record;
            const entity = spawnEntity(rec, this._board, this.game.theme);
            if (!entity) return;
            if (entity instanceof PlayerEntity) {
                this._player = entity;
                this._spawnCell = { c: record.c, r: record.r };
                this._startPower = record.props?.startPower === 'super' ? 'super' : 'small';
            } else if (entity.isCameraZone) {
                // Camera zones are pure logic regions — kept in their own
                // list (never collided / ticked as an enemy). The camera
                // reads them each frame to drive scripted zoom moments.
                this._cameraZones.push(entity);
            } else if (entity.isMovingPlatform) {
                // Moving platforms live in their own list: they must
                // step BEFORE the player + entities (rider-carry order)
                // and they're never collided as enemies — the physics
                // lands bodies on them through `board.movingPlatforms`.
                this._platforms.push(entity);
            } else if (entity.isLight) {
                // Point lights are lighting-only markers — kept in their own
                // list and handed to the lightmap compositor (never collided
                // or ticked as an enemy).
                this._lights.push(entity);
            } else if (entity.isNote) {
                // Music notes are routed through PlatformerNotes for
                // edge-triggered playback, but still tick + render like a
                // normal entity (so the bob/pulse animation runs and the
                // overlap sweep can fire `onCollidePlayer` -> 'note'). The
                // entity id from the stage JSON is stashed so the
                // controller's per-note state map keys off something stable.
                entity.id = record.id;
                this._notes.push(entity);
                this._entities.push(entity);
            } else {
                // Particle emitters that collide with / are dragged by the
                // player need the live player AABB + velocity. Hand them a
                // lazy getter that snapshots a plain box each frame so it
                // stays correct across respawns/power changes. Player vx/vy
                // are px/frame, so scale to px/s for the particle system.
                if (typeof entity.setCollisionBodies === 'function') {
                    entity.setCollisionBodies(() => {
                        const pl = this._player;
                        if (!pl || !pl.alive) return [];
                        return [{
                            x: pl.x, y: pl.y, w: pl.w, h: pl.h,
                            vx: (pl.vx || 0) * 60, vy: (pl.vy || 0) * 60,
                        }];
                    });
                }
                // Area triggers with effect='sound' need their `_inside`
                // edge flag cleared when the player walks out so a later
                // re-entry plays the clip again. Track them separately
                // so the per-frame sweep doesn't have to scan everyone.
                if (record.type === 'area.trigger'
                    && (record.props?.effect === 'sound' || record.props?.effect === 'music')) {
                    // Both effects are edge-triggered (fire-once-per-entry);
                    // the per-frame sweep clears `_inside` when the player
                    // leaves so the next entry can fire again.
                    this._soundZones.push(entity);
                }
                this._entities.push(entity);
            }
            this._board.container.addChild(entity.display);
        });

        // Expose the platforms to the physics: the Y-pass lands bodies
        // on them and isGrounded treats their tops as floor.
        this._board.movingPlatforms = this._platforms;

        // Hand the controller the live notes list AFTER all entities are
        // spawned so it can group them by audio + assign slice indices in
        // one pass. Safe to call with an empty list.
        try { this._notesController?.mount(stage, this._notes); } catch (err) {
            console.warn('[PlatformerScene] notes mount failed:', err?.message || err);
        }

        if (!this._player) {
            // Stage has no player.spawn — author should fix this, but
            // we synthesise one in the top-left to stop the scene
            // crashing. Logged loudly so the bug is visible.
            console.warn('[PlatformerScene] Stage has no player.spawn entity. Synthesising one at (1,1).');
            this._player = new PlayerEntity(1, 1, this._board, this.game.theme, { power: 'small' });
            this._board.container.addChild(this._player.display);
        }

        // Configure the camera for this stage (view + world bounds + the
        // authored base zoom) and snap it onto the player so there's no
        // glide-in from the previous run.
        const camCfg = (stage.camera && typeof stage.camera === 'object') ? stage.camera : {};
        this._camera.configure({
            viewW: this.game.width,
            viewH: this.game.height,
            worldW: this._board.pixelWidth,
            worldH: this._board.pixelHeight,
            baseZoom: Number.isFinite(camCfg.zoom) ? camCfg.zoom : 1,
            followSmooth: Number.isFinite(camCfg.smooth) ? camCfg.smooth : undefined,
        });

        // Lighting: apply the stage's `lighting` block (master switch +
        // ambient + directional) and hand the compositor this stage's point
        // lights. With lighting off the whole pass is skipped (zero cost).
        if (this._lighting) {
            this._lighting.configure(stage.lighting);
            this._lighting.setLights(this._lights);
        }

        // Game-feel layer (SFX, dust, squash & stretch, shake, hit-stop).
        try { this._juice?.destroy(); } catch (_) {}
        this._juice = new PlatformerJuice({
            camera: this._camera,
            worldContainer: this._board.container,
            requestHitStop: (frames) => {
                this._hitStopFrames = Math.max(this._hitStopFrames, frames | 0);
            },
        });

        this._snapCamera();

        this._refreshHud();
    }

    // ── HUD + chrome ──────────────────────────────────────────────
    // The whole UI shell (HUD row, SceneChrome, editor shortcut, and
    // the end-of-run overlays) lives in PlatformerSceneUi.js — this
    // file keeps simulation + flow only.

    _refreshHud() { refreshHud(this); }

    // ── Frame loop ─────────────────────────────────────────────────

    /**
     * Fixed-timestep accumulator (industry standard "fix your
     * timestep"). Pixi's ticker hands us `delta` in 60 Hz frame units
     * (1.0 = one frame at 60 fps), and EVERY physics constant in the
     * game is tuned in pixels-per-frame at that rate. Stepping the
     * simulation in whole 1.0 increments keeps all of that tuning
     * valid while making gameplay speed independent of the display:
     * a 120 Hz monitor accumulates 0.5 per tick and steps every other
     * frame; a laggy 30 fps frame accumulates 2.0 and runs two catch-up
     * steps. The accumulator is clamped to `_MAX_CATCHUP_FRAMES` so a
     * long GC pause / background-tab wake never triggers a spiral of
     * death — beyond the clamp the game slows down rather than
     * teleporting entities through walls.
     */
    static _MAX_CATCHUP_FRAMES = 4;

    /** Fractional 60 Hz frames carried over to the next RAF tick. */
    _accumulator = 0;

    update(delta) {
        if (!this._booted || !this._board || !this._player) {
            // Tick overlays even before the stage finishes loading so
            // the "Tap to restart" pulse keeps animating mid-load.
            this._gameOverOverlay?.update?.(delta);
            this._winOverlay?.update?.(delta);
            return;
        }

        if (this._gameOver || this._won) {
            this._gameOverOverlay?.update?.(delta);
            this._winOverlay?.update?.(delta);
            return;
        }

        // ── simulate: 0..N whole fixed steps ──
        this._accumulator = Math.min(
            this._accumulator + delta,
            this.constructor._MAX_CATCHUP_FRAMES,
        );
        while (this._accumulator >= 1) {
            this._accumulator -= 1;
            this._stepSimulation();
            // A step can flip the scene into win / game-over; stop
            // immediately — the overlay ticks from the next frame.
            if (this._gameOver || this._won) return;
        }

        // ── render-rate work (real elapsed seconds, smooth at any Hz) ──
        // During the death freeze the world is deliberately frozen in
        // place: no background drift, no camera glide.
        if (this._deathFreezeFrames > 0) return;
        this._board.tickBackgrounds?.(delta / 60);
        this._scrollCamera(delta / 60);
        this._refreshHud();
    }

    /** One fixed 60 Hz simulation step (delta = exactly one frame). */
    _stepSimulation() {
        // Death freeze: short delay between dying and respawn /
        // game over so the player can see WHAT killed them. The
        // world keeps rendering but no entities tick.
        if (this._deathFreezeFrames > 0) {
            this._deathFreezeFrames -= 1;
            if (this._deathFreezeFrames === 0) {
                this._handleAfterDeath();
            }
            return;
        }

        // Hit-stop: a few deliberately frozen frames on stomp / damage
        // so impacts land with weight (Vlambeer-style).
        if (this._hitStopFrames > 0) {
            this._hitStopFrames -= 1;
            return;
        }

        this._timeFrames += 1;

        // Moving platforms step FIRST, then riders inherit this frame's
        // platform delta before their own integration — the standard
        // "move riders first" order that keeps a standing player glued
        // to the platform with zero drift at any speed.
        if (this._platforms.length) {
            for (const p of this._platforms) p.update();
            const riders = [this._player];
            for (const e of this._entities) {
                // Only gravity-bound bodies ride (walkers — goombas,
                // koopas, mushrooms). Static pickups / zones don't.
                if (e.alive && Number.isFinite(e.vy)) riders.push(e);
            }
            carryRiders(this._platforms, riders);
        }

        const controls = this._readControls();
        this._player.update(controls, this._board);

        // Juice first (it consumes the player's just-jumped / just-landed
        // flags and peeks at _lastBump before the bonk handler clears it).
        this._juice?.update(this._player);

        // ?-block / brick bonk reaction — bricks vanish, ?-blocks
        // become hard blocks and (depending on the block's `behavior`)
        // either drop a coin into the score or pop out a mushroom.
        if (this._player._lastBump) {
            const bumped = this._player._lastBump;
            this._player._lastBump = null;
            const tile = this._board.tileAt(bumped.c, bumped.r);
            if (tile) {
                const def = PLATFORMER_TILE_DEFS[tile.type];
                if (tile.type === 'brick') {
                    this._board.removeTile(bumped.c, bumped.r);
                } else if (def?.behavior === 'question' || def?.behavior === 'coin-block') {
                    // Pop the question block: replace with hard block
                    // and decide what to drop. The first ?-block in
                    // World 1-1 famously hides a mushroom; here we
                    // make it deterministic — the LEFTMOST ?-block
                    // on each row drops a mushroom, the others drop
                    // coins. Designers can override this in v2 by
                    // adding a `dropType` prop to the tile.
                    this._board.replaceTile(bumped.c, bumped.r, 'block.hard');
                    const drop = this._chooseBlockDrop(bumped);
                    if (drop === 'mushroom') {
                        const m = new MushroomEntity(bumped.c, bumped.r - 1, this._board, this.game.theme,
                            { dir: 'right', kind: 'super', emerge: true });
                        this._entities.push(m);
                        this._board.container.addChild(m.display);
                    } else {
                        // Coin pop: instant score, no entity needed.
                        this._coins += 1;
                        this._score += PLATFORMER_SCORING.pointsPerCoin;
                    }
                }
            }
        }

        // Tick non-player entities. Skip dead ones; they're left in
        // the array so the order stays stable for next-frame iteration.
        for (const e of this._entities) {
            if (e.alive) e.update?.(controls, this._board);
        }

        // Collision sweep: player vs every alive entity. The entity's
        // `onCollidePlayer` returns a verdict the scene reacts to.
        for (const e of this._entities) {
            if (!e.alive) continue;
            // Godot-style filter: skip the pair unless their collision
            // layers/masks interact (unconfigured entities always do, so
            // legacy stages behave exactly as before)…
            if (!masksInteract(this._player, e)) continue;
            // …then the shape-aware overlap test (rect / circle; a
            // default full-box rect is identical to the old AABB).
            if (!shapesOverlap(this._player, e)) continue;
            const verdict = e.onCollidePlayer(this._player);
            if (verdict === 'pickup') {
                this._coins += 1;
                this._score += PLATFORMER_SCORING.pointsPerCoin;
                this._juice?.onVerdict('pickup', e);
            } else if (verdict === 'powerup') {
                this._player.powerUp();
                this._score += PLATFORMER_SCORING.pointsPerMushroom;
                this._juice?.onVerdict('powerup', e);
            } else if (verdict === 'stomp') {
                this._score += PLATFORMER_SCORING.pointsPerStomp;
                this._juice?.onVerdict('stomp', e);
            } else if (verdict === 'damage') {
                // Only juice a hit that actually lands — i-frames absorb
                // the contact silently (takeDamage is a no-op then).
                const hadIframes = this._player.invincibleTimer > 0;
                if (this._player.takeDamage()) {
                    this._beginDeathFreeze();
                    this._juice?.onVerdict('damage', e);
                } else if (!hadIframes) {
                    this._juice?.onVerdict('damage', e);
                }
            } else if (verdict === 'checkpoint') {
                // Mid-level respawn marker: deaths from here on put the
                // player back at the checkpoint instead of the spawn.
                this._spawnCell = { c: e.cell.c, r: e.cell.r };
                this._juice?.onVerdict('pickup', e);
            } else if (verdict === 'win') {
                this._handleWin();
                return;
            } else if (verdict === 'sound') {
                // Area trigger with effect='sound' — forward to the
                // notes controller, which owns the audio engine.
                try { this._notesController?.handleSoundZone?.(e); } catch (_) {}
            } else if (verdict === 'music') {
                // Area trigger with effect='music' — start, stop, or
                // crossfade the long-running background music track.
                try { this._notesController?.handleMusicZone?.(e); } catch (_) {}
            }
            // Note: NoteEntity returns 'note' on every overlap, but the
            // PlatformerNotes controller does its own per-frame edge
            // detection (and de-dupes), so we don't react inline here.
        }

        // Re-arm sound zones whose player has just walked out so the
        // next entry replays the clip. Cheap: only the small list of
        // effect=sound triggers, not every entity.
        for (const z of this._soundZones) {
            if (!z._inside) continue;
            const overlap = z.x < this._player.x + this._player.w
                && z.x + z.w > this._player.x
                && z.y < this._player.y + this._player.h
                && z.y + z.h > this._player.y;
            if (!overlap) z._inside = false;
        }

        // Notes controller per-frame tick — runs its own edge detection
        // against the player AABB and triggers playback as needed.
        try { this._notesController?.update?.(this._player); } catch (_) {}

        // Pit check — falling below the world bottom kills regardless
        // of power state. Trigger the death freeze the same way an
        // enemy hit does so respawn / game over follow the same path.
        if (this._player.y > this._board.pixelHeight + 200) {
            this._player.fellInPit();
            this._beginDeathFreeze();
        }
    }

    /**
     * Read held + edge-triggered input through the InputSystem's
     * action layer — keyboard and any connected gamepad both feed the
     * same actions, so a controller Just Works with no scene code.
     * Returns a stable snapshot the player + entities consume.
     */
    _readControls() {
        const input = this.game.input;
        input?.pollGamepads?.();
        const left  = !!input?.isActionDown?.('left');
        const right = !!input?.isActionDown?.('right');
        const jumpHeld = !!input?.isActionDown?.('jump');
        const run = !!input?.isActionDown?.('run');
        const jumpPressed = jumpHeld && !this._prevJumpDown;
        this._prevJumpDown = jumpHeld;
        return { left, right, jumpHeld, jumpPressed, run };
    }

    /**
     * Decide what a popped ?-block drops. World-1-1 deterministic
     * rule: the FIRST ?-block on a row drops a mushroom, the rest
     * drop coins. Keeps stage authoring stable without designers
     * having to set `dropType` manually.
     */
    _chooseBlockDrop(bumped) {
        // Walk leftward from the bumped tile on the same row; if we
        // find another `?-block` cell still standing we're not the
        // leftmost → drop a coin.
        for (let c = bumped.c - 1; c >= 0; c--) {
            const t = this._board.tileAt(c, bumped.r);
            if (t && t.type === 'block.question') return 'coin';
        }
        // Leftmost ?-block on the row → mushroom drop. (Slightly
        // generous: drops a mushroom every time the player only ever
        // bonks the leftmost one, which feels right for World 1-1.)
        return 'mushroom';
    }

    /**
     * Advance + apply the follow/zoom camera. `dt` is seconds. The camera
     * centres on the player at the stage's base zoom, unless the player is
     * inside a Camera Zone — then it eases to that zone's zoom (and either
     * keeps following the player or locks to the zone centre). It writes the
     * world container's scale + position and feeds the resulting world-space
     * top-left into the board parallax so backgrounds scroll correctly at
     * any zoom.
     */
    _scrollCamera(dt = 1 / 60) {
        if (!this._board || !this._player || !this._camera) return;
        const pcx = this._player.x + this._player.w / 2;
        const pcy = this._player.y + this._player.h / 2;
        this._applyCameraZone(pcx, pcy);
        this._camera.update(dt, pcx, pcy);
        const cam = this._camera.apply(this._worldContainer);
        this._board.applyParallax(cam.camX, cam.camY);
        this._lighting?.update(dt, { camX: cam.camX, camY: cam.camY, zoom: this._camera.zoom });
    }

    /**
     * Pick the topmost Camera Zone the player centre is inside (later =
     * higher z) and set it as the camera override, or clear the override
     * when the player is in open level. Shared by the per-frame scroll and
     * the snap-on-mount/respawn path.
     */
    _applyCameraZone(pcx, pcy) {
        let zone = null;
        for (const z of this._cameraZones) {
            if (z.alive && z.containsPoint(pcx, pcy)) zone = z;
        }
        if (zone) {
            const fx = zone.focusMode === 'zone' ? zone.focusX : null;
            const fy = zone.focusMode === 'zone' ? zone.focusY : null;
            this._camera.setOverride(zone.zoom, fx, fy);
        } else {
            this._camera.clearOverride();
        }
    }

    /** Jump the camera straight onto the player (no glide) — mount/respawn. */
    _snapCamera() {
        if (!this._board || !this._player || !this._camera) return;
        const pcx = this._player.x + this._player.w / 2;
        const pcy = this._player.y + this._player.h / 2;
        this._applyCameraZone(pcx, pcy);
        this._camera.snap(pcx, pcy);
        const cam = this._camera.apply(this._worldContainer);
        this._board.applyParallax(cam.camX, cam.camY);
        this._lighting?.update(0, { camX: cam.camX, camY: cam.camY, zoom: this._camera.zoom });
    }

    // ── Death + win ────────────────────────────────────────────────

    _beginDeathFreeze() {
        // 0.7-second freeze so the player sees the moment of death.
        this._deathFreezeFrames = 42;
    }

    _handleAfterDeath() {
        if (this._readSetting('infiniteLives', false)) {
            this._respawn();
            return;
        }
        this._lives -= 1;
        if (this._lives <= 0) {
            this._showGameOver();
            return;
        }
        this._respawn();
    }

    _respawn() {
        // Re-seed the player at the spawn cell with the configured
        // starting power, then clear the death freeze. Entities that
        // got killed (stomped enemies, picked-up coins) stay dead so
        // the run isn't reset to the start of the stage — the player
        // gets to keep their progress, classic-Mario-3 style.
        const cs = this._board.cellSize;
        const w = PLATFORMER_PLAYER.width;
        const h = PLATFORMER_PLAYER.height;
        this._player.x = this._spawnCell.c * cs + (cs - w) / 2;
        this._player.y = (this._spawnCell.r + 1) * cs - h;
        this._player.vx = 0;
        this._player.vy = 0;
        this._player.alive = true;
        this._player.power = this._startPower;
        this._player.invincibleTimer = PLATFORMER_PLAYER.hitInvincibleFrames;
        this._player._buildVisual();
        // Wipe the notes controller's edge-detect state so the player
        // can sweep through the note sequence again from a clean start.
        try { this._notesController?.reset?.(); } catch (_) {}
        // Re-centre the camera on the respawned player instantly.
        this._snapCamera();
    }

    _showGameOver() { showGameOver(this); }

    _restartFromGameOver() {
        // Cleanest restart is to rebuild the entire scene by switching
        // to ourselves — `init()` will re-seed lives + score and the
        // stage will mount fresh.
        this._cleanupOverlays();
        this.game.sceneManager.switchScene('platformer');
    }

    _handleWin() { showWin(this); }

    _cleanupOverlays() {
        try { this._gameOverOverlay?.destroy?.(); } catch (e) {}
        try { this._winOverlay?.destroy?.(); } catch (e) {}
        try { this._scoreSubmitOverlay?.destroy?.(); } catch (e) {}
        this._gameOverOverlay = null;
        this._winOverlay = null;
        this._scoreSubmitOverlay = null;
    }

    destroy() {
        this._disposed = true;
        try { this._preloadedAudio?.destroy?.(); } catch (e) {}
        this._preloadedAudio = null;
        this._cleanupOverlays();
        try { this._chrome?.destroy?.(); } catch (e) {}
        this._chrome = null;
        if (this._editorTestBtn) {
            try {
                this._editorTestBtn.removeAllListeners?.();
                if (this._editorTestBtn.parent) this._editorTestBtn.parent.removeChild(this._editorTestBtn);
            } catch (e) {}
            this._editorTestBtn = null;
        }
        if (this._lighting) {
            try { this._lighting.destroy(); } catch (e) {}
            this._lighting = null;
        }
        this._lights = [];
        // Tear down audio first so any in-flight scheduled playback is
        // stopped before its entity nodes get removed underneath it.
        try { this._notesController?.destroy?.(); } catch (e) {}
        this._notesController = null;
        try { this._juice?.destroy(); } catch (e) {}
        this._juice = null;
        this._notes = [];
        this._soundZones = [];
        if (this._board) {
            try { this._board.destroy(); } catch (e) {}
            this._board = null;
        }
        for (const e of this._entities) {
            try { e.destroy?.(); } catch (err) {}
        }
        this._entities = [];
        for (const p of this._platforms) {
            try { p.destroy?.(); } catch (err) {}
        }
        this._platforms = [];
        try { this._player?.destroy?.(); } catch (e) {}
        this._player = null;
        this._booted = false;
        super.destroy();
    }
}
